import * as React from "react";
import { useState, useEffect } from "react";
import { Button, DatePicker, Form, Popover, Row, Select, Tooltip, Col, Input } from "antd";
import { Link, useHistory } from "react-router-dom";
import moment from "moment";
import { v4 as uuid } from "uuid";
import { PlusOutlined } from "@ant-design/icons";
import LocalStorageService from "../../services/LocalStorageService";
import {
  toDisplayFormat,
  toDisplayFormatWithTime,
  toHttpFormatStartOfDay,
} from "../../lib/utils/formatDate";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import "./AccountListTs.less";
import {
  ACCOUNT_CREATE,
  MENU_ACCOUNTS_WRITE,
  MENU_ACCOUNTS_ORGANIZATION_WRITE,
} from "../../lib/const/permissions";
import { hasAccess } from "../RolesModule/hasAccess";
import { ReactComponent as SubmitButtonIcon } from "../../img/buttons_icons/submitButton_icon.svg";
import { ReactComponent as CancelButtonIcon } from "../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../img/versions_icons/addVersion_icon.svg";
import { ReactComponent as LoupeIcon } from "../../img/loupe-icon-currentColor.svg";
import { Subscriber } from "../../lib/interfaces/Subscriber";
import { Organization } from "../../lib/interfaces/Organization";
import { useAppDispatch, useAppSelector } from "../../index";
import { createAccountVersion } from "../../store/slices/accountsTs";
import SearchFilters from "./SearchFilters";
import { FormFieldValue } from "../../lib/interfaces/FormFieldValue";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import { getOrganizationIdentityAttribute } from "../../lib/utils/getTenantProperty";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import usePhoneMask from "../../lib/hooks/usePhoneMask";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import featuresToggle from "../../lib/utils/featuresToggle";
import { hasLocalAccess } from "../../lib/configs";
import { getBlockingStatusIcon } from "../../lib/utils/getBlockingStatusIcon";
import BlockingModal from "../BlockingModal/BlockingModal";
import { BlockingObjTypes } from "../../lib/const/blockingObjTypes";
import useBlockingMode from "../../lib/hooks/useBlockingMode";
import { geDicValueById } from "../../lib/utils";

const { Option } = Select;

interface AccountVersionRow {
  key: string;
  id: number;
  bin?: string;
  externalId: string[];
  phones?: string[];
  fullName: string;
  shortName: string;
  regionId?: number;
  countryId?: number;
  fromDate: string;
  toDate: string;
  accountStatusId: number;
  isCurrentVersion: boolean;
  isPastVersion: boolean;
  isLatestVersion: boolean;
  isCreated: boolean;
  subscribers: Subscriber[];
  organization?: Organization;
  accTypeId: number;
}

interface IAccountList {
  handlePageableSearch: (values: FormFieldValue[]) => void;
  getRowVersions: (params: {
    accountId: number;
    organizationId: number;
    subscribersId: number[];
  }) => void;
  isPageableSearch: boolean;
  setIsPageableSearch: React.Dispatch<React.SetStateAction<boolean>>;
  advancedSearchFormValues: FormFieldValue[];
  setAdvancedSearchFormValues: React.Dispatch<React.SetStateAction<FormFieldValue[] | undefined>>;
  searchByNameFields: FormFieldValue[];
  setSearchByNameFields: React.Dispatch<React.SetStateAction<FormFieldValue[] | undefined>>;
}

interface AccountTableRow {
  key: number;
  id: number;
  externalId: string[];
  bin: any;
  phones?: string[];
  fullName: string;
  shortName: string;
  regionId?: number;
  countryId?: number;
  fromDate: string;
  toDate: string;
  accountStatusId: number;
  organizationId: number;
  subscribersId: number[];
  blockingStatus: number | null;
  accTypeId: number;
}

function AccountListTs(props: IAccountList) {
  const {
    getRowVersions,
    handlePageableSearch,
    isPageableSearch,
    setIsPageableSearch,
    advancedSearchFormValues,
    setAdvancedSearchFormValues,
    searchByNameFields,
    setSearchByNameFields,
  } = props;
  const { accountsDto, accountsDtoLoading, accountListRowData } = useAppSelector(
    (state) => state.accountsTs
  );
  const {
    accountStatuses,
    accountListRowDicData,
    regions,
    countries,
    accountTypes,
  } = useAppSelector((state) => state.dicts);

  const {
    accountVersionsLoading,
    organizationVersionsLoading,
    subscribersVersionsLoading,
    accountVersions,
    organizationVersions,
    subscribersVersions,
  } = accountListRowData;
  const { organizationDicVersions, organizationDicVersionsLoading } = accountListRowDicData;

  const isBlockingMode = useBlockingMode();

  const [dataSource, setDataSource] = useState<AccountTableRow[]>([]);
  const [childDataSource, setChildDataSource] = useState<AccountVersionRow[]>([]);
  const [isDisabledButtons, setIsDisabledButtons] = useState(false);
  const [advancedSearchForm] = Form.useForm();
  const [searchByNameForm] = Form.useForm();
  const [versionsTableForm] = Form.useForm();

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);
  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childDataSource);

  const { getFullPhoneToDisplay } = usePhoneMask();

  const dispatch = useAppDispatch();
  const history = useHistory();

  const showHistoryModal = (rec) => {
    setHistoryModalVisible(true);
    setObjectId(rec.id);
  };

  const [objectBlockingId, setObjectBlockingId] = useState<{ id?: number; fullName?: string }>({});

  const handleShowBlockingModal = (objId) => {
    setObjectBlockingId(objId);
  };

  const handleHideBlockingModal = () => {
    setObjectBlockingId({});
  };

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    id: "6%",
    fullName: "12%",
    shortName: "12%",
    regionId: "9%",
    countryId: "9%",
    externalId: "11%",
    phones: "12%",
    bin: "12%",
    accountStatusId: "10%",
    period: "22%",
    buttons: "1%",
    blockingIco: "50px",
    accTypeId: "10%",
  };

  const usedColumnKeys = [
    "id",
    "fullName",
    "blockingStatus",
    "accTypeId",
    "externalId",
    "bin",
    "phones",
    "accountStatusId",
    "period",
    "actions",
    "buttons",
  ];

  const getColumns = () =>
    [
      {
        title: "ID аккаунта",
        dataIndex: "id",
        key: "id",
        width: columnsWidths.id,
        hidden: true,
      },
      {
        title: "Аккаунт",
        dataIndex: "fullName",
        key: "fullName",
        width: columnsWidths.fullName,
        hidden: false,
      },
      {
        title: "",
        dataIndex: "blockingStatus",
        key: "blockingStatus",
        width: columnsWidths.blockingIco,
        hidden: false,
        render: (blockingStatus, record) => (
          <div>{getBlockingStatusIcon(blockingStatus, () => handleShowBlockingModal(record))}</div>
        ),
      },
      {
        title: "Тип аккаунта",
        dataIndex: "accTypeId",
        key: "accTypeId",
        width: columnsWidths.accTypeId,
        hidden: true,
        render: (accTypeId) => geDicValueById(accountTypes, accTypeId)?.name,
      },
      {
        title: "Код",
        dataIndex: "shortName",
        key: "shortName",
        width: columnsWidths.shortName,
        hidden: false,
      },
      {
        title: "Регион",
        dataIndex: "regionId",
        key: "regionId",
        width: columnsWidths.regionId,
        hidden: false,
        render: (regionId) => geDicValueById(regions, regionId)?.name,
      },
      {
        title: "Страна",
        dataIndex: "countryId",
        key: "countryId",
        width: columnsWidths.countryId,
        hidden: false,
        render: (countryId) => geDicValueById(countries, countryId)?.name,
      },
      {
        title: "Технический ID",
        dataIndex: "externalId",
        key: "externalId",
        width: columnsWidths.externalId,
        hidden: false,
        render: (text, record) => {
          if (record.externalId?.length > 1) {
            return (
              <span
                aria-hidden="true"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Popover
                  placement="topLeft"
                  content={() => (
                    <ul className="list-popover">
                      {record.externalId.map((item, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                >
                  <p style={{ margin: 0 }}>{`${record.externalId[0].trim()},...`}</p>
                </Popover>
              </span>
            );
          }
          return text;
        },
      },
      {
        title: LocalStorageService.getTenantId() === "1" ? "БИН" : "ИНН",
        dataIndex: "bin",
        key: "bin",
        hidden: false,
        width: columnsWidths.bin,
      },
      {
        title: "Контактный телефон",
        dataIndex: "phones",
        key: "phones",
        width: columnsWidths.phones,
        hidden: false,
        render: (phones) => {
          if (phones?.length > 1) {
            return (
              <span
                aria-hidden="true"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Popover
                  placement="topLeft"
                  content={() => (
                    <ul className="list-popover">
                      {phones.map((item, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                >
                  <p style={{ margin: 0 }}>{`${phones[0].trim()},...`}</p>
                </Popover>
              </span>
            );
          }
          return phones?.[0] || "";
        },
      },
      {
        title: "Статус",
        dataIndex: "accountStatusId",
        key: "accountStatusId",
        width: columnsWidths.accountStatusId,
        hidden: false,
        render: (accountStatusId) => geDicValueById(accountStatuses, accountStatusId)?.name,
      },
      {
        title: "Период",
        dataIndex: "period",
        key: "period",
        width: columnsWidths.period,
        hidden: false,
        render: (value, record) =>
          `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`,
      },
      {
        dataIndex: "buttons",
        key: "buttons",
        width: columnsWidths.buttons,
        className: "history-column",
        style: { display: "flex", justifyContent: "end" },
        hidden: false,
        render: (value, record) => (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal(record);
            }}
            className="iconButton"
            type="text"
            htmlType="button"
            icon={<DotsIcon />}
          />
        ),
      },
    ]
      .filter(({ key }) => usedColumnKeys.includes(key))
      .filter(({ key }) => !(key === "blockingStatus" && !isBlockingMode));

  const [columns, setColumns] = useState(getColumns());

  useEffect(() => {
    setColumns((prev) => {
      const newColumns = getColumns().map((column, i) => {
        const prevColumnFind = prev.find((el) => el.key === column.key);

        return {
          ...column,
          hidden: prevColumnFind?.hidden !== undefined ? prevColumnFind.hidden : column.hidden,
        };
      });

      return newColumns;
    });
  }, [accountStatuses, isBlockingMode, accountTypes]);

  const addVersion = (record: AccountVersionRow) => {
    setChildDataSource((prevState): AccountVersionRow[] =>
      [
        ...prevState,
        {
          ...record,
          key: uuid(),
          isCurrentVersion: false,
          isPastVersion: false,
          isLatestVersion: false,
          isCreated: true,
        },
      ].sort((a, b) => (moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1))
    );
  };

  const getChildColumns = () =>
    [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: columnsWidths.id,
        hidden: true,
      },
      {
        title: "Аккаунт",
        dataIndex: "fullName",
        key: "fullName",
        width: columnsWidths.fullName,
        hidden: false,
      },
      {
        title: "",
        dataIndex: "blockingStatus",
        key: "blockingStatus",
        width: columnsWidths.blockingIco,
        hidden: false,
        render: (blockingStatus) => <div>{getBlockingStatusIcon(undefined)}</div>,
      },
      {
        title: "Тип аккаунта",
        dataIndex: "accTypeId",
        key: "accTypeId",
        width: columnsWidths.accTypeId,
        hidden: true,
        render: (accTypeId) => geDicValueById(accountTypes, accTypeId)?.name,
      },
      {
        title: "Код",
        dataIndex: "shortName",
        key: "shortName",
        width: columnsWidths.shortName,
        hidden: false,
      },
      {
        title: "Регион",
        dataIndex: "regionId",
        key: "regionId",
        width: columnsWidths.regionId,
        hidden: false,
        render: (regionId) => geDicValueById(regions, regionId)?.name,
      },
      {
        title: "Страна",
        dataIndex: "countryId",
        key: "countryId",
        width: columnsWidths.countryId,
        hidden: false,
        render: (countryId) => geDicValueById(countries, countryId)?.name,
      },
      {
        title: "Технический ID",
        dataIndex: "externalId",
        key: "externalId",
        width: columnsWidths.externalId,
        hidden: false,
        render: (text, record) => {
          if (record.externalId?.length > 1) {
            return (
              <span
                aria-hidden="true"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Popover
                  placement="topLeft"
                  content={() => (
                    <ul className="list-popover">
                      {record.externalId.map((item, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                >
                  <p style={{ margin: 0 }}>{`${record.externalId[0].trim()},...`}</p>
                </Popover>
              </span>
            );
          }
          return text;
        },
      },
      {
        title: LocalStorageService.getTenantId() === "1" ? "БИН" : "ИНН",
        dataIndex: "bin",
        key: "bin",
        width: columnsWidths.bin,
        hidden: false,
      },
      {
        title: "Контактный телефон",
        dataIndex: "phones",
        key: "phones",
        width: columnsWidths.phones,
        hidden: false,
        render: (phones) => {
          if (phones?.length > 1) {
            return (
              <span
                aria-hidden="true"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Popover
                  placement="topLeft"
                  content={() => (
                    <ul className="list-popover">
                      {phones.map((item, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                >
                  <p style={{ margin: 0 }}>{`${phones[0].trim()},...`}</p>
                </Popover>
              </span>
            );
          }
          return phones?.[0] || "";
        },
      },
      {
        title: "Статус",
        dataIndex: "accountStatusId",
        key: "accountStatusId",
        width: columnsWidths.accountStatusId,
        hidden: false,
        render: (accountStatusId, record) => {
          if (record.isCreated) {
            return (
              <Form.Item
                name="accountStatusId"
                rules={[{ required: true }]}
                className="form-item-required__hidden-error__mb0"
              >
                <Select style={{ width: "150px" }}>
                  {accountStatuses.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }
          return geDicValueById(accountStatuses, accountStatusId)?.name;
        },
      },
      {
        title: "Период",
        dataIndex: "period",
        key: "period",
        width: columnsWidths.period,
        hidden: false,
        render: (value, record) => {
          if (record.isCreated) {
            return (
              <Row align="middle">
                <Form.Item
                  style={{ marginRight: "5px" }}
                  name="fromDate"
                  rules={[{ required: true }]}
                  className="form-item-required__hidden-error__mb0"
                >
                  <DatePicker
                    defaultPickerValue={moment(record.fromDate).add(1, "day")}
                    disabledDate={(current) =>
                      current <= moment(record.fromDate) || current > moment(record.toDate)
                    }
                  />
                </Form.Item>
                <span>- {toDisplayFormat(record.toDate)}</span>
              </Row>
            );
          }
          return `${toDisplayFormatWithTime(record.fromDate)} - ${toDisplayFormatWithTime(
            record.toDate
          )}`;
        },
      },
      {
        dataIndex: "actions",
        key: "actions",
        width: columnsWidths.actions,
        hidden: false,
        render: (value, record) => {
          if (hasAccess(MENU_ACCOUNTS_ORGANIZATION_WRITE, MENU_ACCOUNTS_WRITE)) {
            if (!record.isPastVersion && !record.isCreated) {
              return (
                <Row justify="end">
                  <Tooltip title="Добавить версию">
                    <Button
                      className="iconButton"
                      icon={<AddVersionIcon />}
                      type="text"
                      htmlType="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDisabledButtons(true);
                        addVersion(record);
                      }}
                      disabled={isDisabledButtons}
                    />
                  </Tooltip>
                </Row>
              );
            }
            if (record.isCreated) {
              return (
                <Row>
                  <Tooltip title="Отменить">
                    <Button
                      className="iconButton"
                      icon={<CancelButtonIcon />}
                      type="text"
                      htmlType="button"
                      onClick={() => {
                        versionsTableForm.resetFields();
                        setIsDisabledButtons(false);
                        setChildDataSource((prevState) =>
                          prevState.filter((item) => !item.isCreated)
                        );
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Подтвердить">
                    <Button
                      className="iconButton"
                      icon={<SubmitButtonIcon />}
                      type="text"
                      htmlType="button"
                      onClick={versionsTableForm.submit}
                    />
                  </Tooltip>
                </Row>
              );
            }
          }
          return null;
        },
      },
      {
        dataIndex: "buttons",
        key: "buttons",
        width: "2%",
        className: "history-column",
        style: { display: "flex", justifyContent: "end" },
        render: (v, rec) => (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal(rec);
            }}
            className="iconButton"
            type="text"
            htmlType="button"
            icon={<DotsIcon />}
          />
        ),
      },
    ]
      .filter(({ key }) => usedColumnKeys.includes(key))
      .filter(({ key }) => !(key === "blockingStatus" && !isBlockingMode));

  const [
    childTableColumns,
    setChildTableColumns,
  ] = versionsTableUtils.useChildColumns(getChildColumns, [isChangingForbidden]);

  useEffect(() => {
    setChildTableColumns(getChildColumns());
  }, [isBlockingMode, accountTypes, isDisabledButtons]);

  const onFinish = () => {
    const newVersion = childDataSource.find((item) => item.isCreated);

    const { fromDate, accountStatusId } = versionsTableForm.getFieldsValue();
    if (newVersion) {
      const { id, organization, toDate, subscribers } = newVersion;
      dispatch(
        createAccountVersion({
          accountId: id,
          fromDate: toHttpFormatStartOfDay(fromDate),
          payload: {
            id,
            toDate,
            accountStatusId,
            organization,
            subscribers,
            accTypeId: newVersion.accTypeId,
          },
        })
      ).then(() => {
        if (newVersion.organization) {
          getRowVersions({
            accountId: newVersion.id,
            organizationId: newVersion.organization.id,
            subscribersId: newVersion.subscribers.map((item) => item.id),
          });
          setIsDisabledButtons(false);
        }
      });
    }
  };

  useEffect(() => {
    const currentDate = moment();
    if (
      checkIsLoaded(
        accountVersionsLoading,
        organizationVersionsLoading,
        subscribersVersionsLoading,
        organizationDicVersionsLoading
      )
    ) {
      setChildDataSource(
        accountVersions
          .map(
            (version, i): AccountVersionRow => {
              const versionOrganization = organizationVersions.find((orgVersion) => {
                if (
                  moment(version.toDate).isSameOrAfter(orgVersion.fromDate) &&
                  moment(version.toDate).isSameOrBefore(orgVersion.toDate)
                ) {
                  return orgVersion;
                }
                return null;
              });
              // @ts-ignore
              const versionSubscription = subscribersVersions.flat().filter((item) => {
                if (
                  moment(version.toDate).isSameOrAfter(item.fromDate) &&
                  moment(version.toDate).isSameOrBefore(item.toDate)
                ) {
                  return item;
                }
                return null;
              });
              return {
                key: version.fromDate.toString().concat(String(version.id)),
                id: version.id,
                externalId: versionSubscription.map((item) => item.externalId),
                bin: organizationDicVersions.find(
                  (item) =>
                    moment(version.toDate).isSameOrAfter(item.fromDate) &&
                    moment(version.toDate).isSameOrBefore(item.toDate)
                )?.stringValue,
                phones: versionOrganization?.phones?.map(({ countryCode, phone }) =>
                  getFullPhoneToDisplay(countryCode, phone)
                ),
                fullName: versionOrganization?.fullName || "",
                shortName: versionOrganization?.shortName || "",
                countryId: versionOrganization?.legalAddress?.countryId,
                regionId: versionOrganization?.legalAddress?.regionId,
                fromDate: version.fromDate,
                toDate: version.toDate,
                accountStatusId: version.accountStatusId,
                isCurrentVersion:
                  moment(currentDate).isSameOrAfter(version.fromDate) &&
                  moment(currentDate).isSameOrBefore(version.toDate),
                isPastVersion: moment(version?.toDate).isBefore(currentDate),
                isLatestVersion: accountVersions.length === i + 1,
                isCreated: false,
                subscribers: versionSubscription,
                organization: versionOrganization,
                accTypeId: version.accTypeId,
              };
            }
          )
          .sort((a, b) => (moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1))
      );
    }
  }, [
    accountVersionsLoading,
    organizationVersionsLoading,
    subscribersVersionsLoading,
    organizationDicVersionsLoading,
  ]);

  useEffect(() => {
    if (checkIsLoaded(accountsDtoLoading) && accountsDto) {
      setDataSource(
        accountsDto.content.map((acc) => ({
          key: acc.id,
          id: acc.id,
          externalId: acc.subscribers.map((item) => `${item.externalId} `),
          bin: acc?.organization?.properties.find(
            (item) => item.propertyType?.alias === getOrganizationIdentityAttribute()
          )?.stringValue,
          phones: acc.organization.phones?.map(({ countryCode, phone }) =>
            getFullPhoneToDisplay(countryCode, phone)
          ),
          fullName: acc.organization?.fullName,
          shortName: acc.organization?.shortName || "",
          countryId: acc.organization?.legalAddress?.countryId,
          regionId: acc.organization?.legalAddress?.regionId,
          fromDate: acc.fromDate,
          toDate: acc.toDate,
          accountStatusId: acc.accountStatusId,
          organizationId: acc?.organization?.id,
          subscribersId: acc.subscribers.map((item) => item.id),
          accTypeId: acc.accTypeId,
          blockingStatus: acc?.blockingStatus,
        }))
      );
    }
  }, [accountsDtoLoading, accountsDto]);

  const handleSearchByName = () => {
    const values = searchByNameForm.getFieldsValue();
    const nameValue = [
      {
        name: "fullName",
        value: values.fullName,
      },
    ];
    setSearchByNameFields(nameValue);
    handlePageableSearch(nameValue);
    setIsPageableSearch(true);
    setAdvancedSearchFormValues(undefined);
    advancedSearchForm.resetFields();
  };

  const resetSearchByName = () => {
    setSearchByNameFields(undefined);
    handlePageableSearch([]);
    setIsPageableSearch(false);
    setAdvancedSearchFormValues(undefined);
  };

  const isCreateAccountAvailable = featuresToggle({
    value: true,
    alternativeValue: false,
    condition: hasLocalAccess("feature.createAccount"),
  });

  return (
    <div className="account-list">
      <HistoryAndPropertyModal
        objectTypeSysname="account"
        objectId={objectId}
        handleHideModal={() => setHistoryModalVisible(false)}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ACCOUNTS_ORGANIZATION_WRITE, MENU_ACCOUNTS_WRITE)}
      />
      <BlockingModal
        objectTypeId={BlockingObjTypes.Аккаунт}
        objectId={objectBlockingId?.id}
        handleHideModal={handleHideBlockingModal}
        visibleModal={!!objectBlockingId?.id}
        accountName={objectBlockingId?.fullName}
      />
      <Row justify="space-between" align="middle">
        <Col>
          <h1 className="main-title">
            {isPageableSearch ? "Список аккаунтов / Результаты поиска" : "Список аккаунтов"}
          </h1>
        </Col>
        <Col>
          <Form form={searchByNameForm} fields={searchByNameFields} onFinish={handleSearchByName}>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const fullName = getFieldValue("fullName");
                const icon = fullName ? (
                  <Tooltip title="Найти">
                    <LoupeIcon
                      style={{
                        color: "#334455",
                        cursor: "pointer",
                      }}
                      onClick={searchByNameForm.submit}
                    />
                  </Tooltip>
                ) : (
                  <LoupeIcon color="#aab9c8ff" />
                );
                return (
                  <Form.Item className="searchByNameInput" name="fullName">
                    <Input
                      placeholder="Поиск по наименованию"
                      suffix={icon}
                      allowClear
                      onChange={(e) => {
                        const { value } = e.target;
                        if (!value) {
                          resetSearchByName();
                        }
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Row justify="end" style={{ marginBottom: 15, paddingRight: 10 }}>
        <Col>
          {hasAccess(ACCOUNT_CREATE) && isCreateAccountAvailable && (
            <Link to="/accounts/create">
              <Button type="primary" icon={<PlusOutlined />}>
                Добавить аккаунт
              </Button>
            </Link>
          )}
        </Col>
      </Row>
      <SearchFilters
        handlePageableSearch={handlePageableSearch}
        setIsPageableSearch={setIsPageableSearch}
        advancedSearchFormValues={advancedSearchFormValues}
        setAdvancedSearchFormValues={setAdvancedSearchFormValues}
        isPageableSearch={isPageableSearch}
        setSearchByNameFields={setSearchByNameFields}
        searchByNameForm={searchByNameForm}
        advancedSearchForm={advancedSearchForm}
      />
      <Form form={versionsTableForm} onFinish={onFinish}>
        <ToggleableVersionsTable
          tableName="account-list-table"
          columns={columns}
          setColumns={setColumns}
          tableData={dataSource}
          childTableColumns={childTableColumns}
          setChildTableColumns={setChildTableColumns}
          childTableData={childDataSource}
          isTableLoading={checkIsLoading(accountsDtoLoading)}
          isChildTableLoading={checkIsLoading(
            accountVersionsLoading,
            organizationVersionsLoading,
            subscribersVersionsLoading,
            organizationDicVersionsLoading
          )}
          onOpenVersionsCallback={(record, expanded) => {
            if (!expanded) {
              getRowVersions({
                accountId: record.id,
                organizationId: record.organizationId,
                subscribersId: record.subscribersId,
              });
            }
          }}
          onCloseVersionsCallback={(record, expanded) => {
            if (expanded) {
              versionsTableForm.resetFields();
              setIsDisabledButtons(false);
              setChildDataSource((prevState) => prevState.filter((item) => !item.isCreated));
            }
          }}
          isPageable={{
            current: accountsDto && accountsDto?.number + 1,
            total: accountsDto && accountsDto?.totalElements,
            showSizeChanger: false,
            onChange: (page: number) => {
              let pageValue;
              pageValue = [
                {
                  name: "page",
                  value: String(page),
                },
              ];

              if (searchByNameFields)
                pageValue = [
                  ...searchByNameFields,
                  {
                    name: "page",
                    value: String(page),
                  },
                ];

              if (advancedSearchFormValues)
                pageValue = [
                  ...advancedSearchFormValues,
                  {
                    name: "page",
                    value: String(page),
                  },
                ];
              handlePageableSearch(pageValue);
            },
          }}
          onRow={(record) => ({
            onClick: () => {
              history.push(`/accounts/${record.id}/info`);
            },
          })}
        />
      </Form>
    </div>
  );
}

export default AccountListTs;
